import React, { Suspense, useMemo, useEffect, useState } from 'react';
import { Button, Container } from '@mui/material';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';

import { getSelectedAccount, setSelectedAccount } from '../../../redux/selectedAccountSlice';
import { getSelectedBuilding, setSelectedBuilding } from '../../../redux/selectedBuildingSlice';
import { getSelectedSubscription } from '../../../redux/selectedSubscriptionSlice';
import { getAllAccounts } from '../../../redux/allAccountsSlice';

import { useGetBuildingsByAccountQuery, useUpdateBuildingMutation, usePostBuildingMutation, usePostBillingMeterOnBuildingMutation, useDeleteBillingMeterOnBuildingMutation, useGetBuildingsQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useGetSubscriptionsByAccountQuery, useGetSubscriptionsQuery } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import enrichBuildings from '../../../utils/enrichBuildings';
import Fade from '@mui/material/Fade';
import { useGetJobSpecificationsQuery } from '../../../api/recalc/endpoints/jobSpecificationApiSlice';
import Slide from '@mui/material/Slide';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';
import SimpleBackdrop from '../../../components/backdrop/SimpleBackdrop';

const BuildingDataGrid = React.lazy(() =>
  import('remote/BuildingDataGrid').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const BuildingCreate = React.lazy(() =>
  import('remote/BuildingCreate').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const FullscreenDialog = React.lazy(() =>
  import('remote/FullscreenDialog').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});


const BuildingsPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const selectedBuilding = useSelector(getSelectedBuilding);
  const allAccounts = useSelector(getAllAccounts);

  const [enrichedBuildings, setEnrichedBuildings] = React.useState([]);
  const { data: jobSpecifications } = useGetJobSpecificationsQuery();
  const { data: buildings, error: errorBuildings, isLoading: isLoadingBuildings, isSuccess: isSuccessBuildings } = useGetBuildingsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount.AccountId });
  const { data: allBuildings, error: errorAllBuildings, isLoading: isLoadingAllBuildings, isSuccess: isSuccessAllBuildings } = useGetBuildingsQuery({skip: selectedAccount.AccountId !== 1});
  const { data: subscriptions, error: errorSubscription, isLoading: isLoadingSubscriptions, isSuccess: isSuccessSubscriptions } = useGetSubscriptionsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount.AccountId });
  const { data: allSubscriptions, error: errorAllSubscriptions, isLoading: isLoadingAllSubscriptions, isSuccess: isSuccessAllSubscriptions } = useGetSubscriptionsQuery({ skip: selectedAccount.AccountId !== 1 });
  const { data: units, error: errorUnits, isLoading: isLoadingUnits, isSuccess: isSuccessUnits } = useGetUnitsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount.AccountId });
  const [postBillingMeterOnBuilding, { isLoading: isPostingBillingMeterOnBuilding, isSuccess: isSuccessPostBillingMeterOnBuilding, isError: isErrorPostBillingMeterOnBuilding, error: errorPostBillingMeterOnBuilding }] = usePostBillingMeterOnBuildingMutation();
  const [deleteBillingMeterOnBuilding, { isLoading: isDeletingBillingMeterOnBuilding, isSuccess: isSuccessDeleteBillingMeterOnBuilding, isError: isErrorDeleteBillingMeterOnBuilding, error: errorDeleteBillingMeterOnBuilding }] = useDeleteBillingMeterOnBuildingMutation();
  const [updateBuilding, { isLoading: isUpdatingBuilding, isSuccess: isSuccessUpdateBuilding, isError: isErrorUpdateBuilding, error: errorUpdateBuilding }] = useUpdateBuildingMutation();
  const [postBuilding, { isSuccess: isSuccessPostBuilding, isLoading, isError: isErrorPostBuilding, error: errorPostBuilding }] = usePostBuildingMutation();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dispatch = useDispatch();

  const handleOpen = () => setDialogOpen(true);
  const handleClose = () => {
    setDialogOpen(false);
  };

  const navigate = useNavigate();

  const handleUpdateBuilding = async ({ buildingId, ...values }) => {
    try {
      const response = await updateBuilding({ buildingId, building: values }).unwrap();
      dispatch(showSnackbar({ message: 'Building was updated', severity: 'success', response: response }));
      setTimeout(() => {
        setDialogOpen(false);
      }, 2000);
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error updating building', severity: 'error', response: response }));
    }
  };

  const handleAddBillingMeter = async ({ buildingId, ...values }) => {
    try {
      const response = await postBillingMeterOnBuilding({ buildingId, billingMeter: values }).unwrap();
      dispatch(showSnackbar({ message: 'Billing meter was added', severity: 'success', response: response }));
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error adding billing meter', severity: 'error', response: response }));
    }
  }

  const handleRemoveBillingMeter = async ({ buildingId, billingMeterId }) => {
    try {
      const response = await deleteBillingMeterOnBuilding({ buildingId, billingMeterId }).unwrap();
      dispatch(showSnackbar({ message: 'Billing meter was removed', severity: 'success', response: response }));
      setTimeout(() => {
        setDialogOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to remove billing meter:', error);
      dispatch(showSnackbar({ message: 'Error removing billing meter', severity: 'error', response: response }));
      setTimeout(() => {
        setDialogOpen(false);
      }, 2000);
    }
  }

  const handleCreateBuilding = async (data) => {
    try {
      const response = await postBuilding(data).unwrap();
      dispatch(showSnackbar({ message: 'Building was created', severity: 'success', response: response }));
      setTimeout(() => {
        setDialogOpen(false);
      }, 2000);
      
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error creating building', severity: 'error', response: response }));
      setTimeout(() => {
        setDialogOpen(false);
      }, 2000);
    }
  };

  const handleBuildingRowClicked = (building) => {
    if (selectedAccount.AccountId !== building.AccountId) {
      dispatch(setSelectedAccount(allAccounts.find(account => account.AccountId === building.AccountId)));
    }
    dispatch(setSelectedBuilding(building));
    navigate(`/buildings/${building.Name}`);
  };

  const enrichedBuildingsMemo = useMemo(() => {
    if (isSuccessAllBuildings && isSuccessAllSubscriptions && selectedAccount.AccountId === 1) {
      return enrichBuildings(allBuildings, allSubscriptions);
    } else if (isSuccessBuildings && isSuccessSubscriptions && selectedAccount.AccountId !== 1) {
      return enrichBuildings(buildings, subscriptions);
    }
    return [];
  }, [buildings, subscriptions, selectedAccount, allBuildings, allSubscriptions]);

  useEffect(() => {
    if (isSuccessBuildings && isSuccessSubscriptions) {
      setEnrichedBuildings(enrichedBuildingsMemo);
    }
  }, [enrichedBuildingsMemo]);

  return (
    <Container maxWidth='xxl'>
      <SimpleBackdrop open={isDeletingBillingMeterOnBuilding || isPostingBillingMeterOnBuilding} />
      <Suspense fallback={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
          <CircularProgress /> 
        </Box>
      }>
         <Fade 
          in={isSuccessBuildings}
          timeout={500}
        >
          <Box>
            <BuildingDataGrid 
              buildings={enrichedBuildings} 
              units={units}
              jobSpecifications={jobSpecifications}
              isLoading={isLoadingBuildings || isLoadingSubscriptions} 
              handleBuildingRowClicked={handleBuildingRowClicked} 
              onSubmit={handleUpdateBuilding}
              handleOpen={handleOpen}
              addBillingMeter={handleAddBillingMeter}
              removeBillingMeter={handleRemoveBillingMeter}
              />
          </Box>
        </Fade>
        <FullscreenDialog
          open={dialogOpen}
          onClose={handleClose}
          title="New Building"
          TransitionComponent={Transition}
        >
        <BuildingCreate 
          selectedAccount={selectedAccount} 
          postBuilding={handleCreateBuilding} 
          handleClose={handleClose}
          isLoading={isLoading}
        />
      </FullscreenDialog>
      </Suspense>
      <Outlet />
    </Container>
  );
};

export default BuildingsPage;